import type React from 'react';
import { useState } from 'react';

import { Typography } from '@cobliteam/ui/components';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Label from '@cobliteam/ui/form/Label';

import { Location, UserPermission, type VehicleGroup } from '@cobliteam/common-types';

import { usePermissions } from '@cobliteam/hooks';

import Icon from '@cobliteam/ui/icons';

import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Placeholder from '@cobliteam/ui/components/Placeholder';
import Tooltip from '@cobliteam/ui/components/Tooltip';

export interface MultiSelectVehicleGroupsSidebarOverlayProps {
  isLoading?: boolean;
  vehicleGroups: VehicleGroup[];
  selectedGroups: VehicleGroup[];
  selectionChanged: boolean;
  disableGroupsWithoutVehicle?: boolean;
  limit?: number;
  onChange: (group: VehicleGroup) => void;
  onClearFilter: () => void;
  onApplyFilter: () => void;
  onCancel: () => void;
}

const MultiSelectVehicleGroupsSidebarOverlay: React.FC<MultiSelectVehicleGroupsSidebarOverlayProps> = ({
  isLoading,
  selectedGroups,
  vehicleGroups,
  selectionChanged,
  disableGroupsWithoutVehicle = true,
  limit,
  onChange,
  onClearFilter,
  onApplyFilter,
  onCancel,
}) => {
  const [searchBoxInput, setSearchBoxInput] = useState('');
  const [selectedGroupsDraft] = useState<VehicleGroup[]>(selectedGroups);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortOrderLabel, setSortOrderLabel] = useState('A-Z');

  const { isAccessAllowed } = usePermissions();
  const hasUpdateVehiclePermission = isAccessAllowed(UserPermission.CREATE_UPDATE_DELETE_VEHICLE);

  const handleCreateGroup = () => {
    window.open(`${window.location.origin}${Location.VEHICLES_GROUPS}/new`, '_blank')!.focus();
  };

  const renderNoGroupsPlaceholder = () => (
    <div>
      <div className='m-7 flex flex-col flex-wrap items-center justify-center gap-3'>
        <Placeholder
          className='m-0 w-60'
          icon='cb-placeholder-vehicle-group'
          text={vehicleGroups.length === 0 ? 'Ainda não existem grupos cadastrados. Crie grupo de veículos para organizar sua frota.' : 'Não existe grupo com esse nome.'}
        />
        {hasUpdateVehiclePermission && (
          <Button onClick={handleCreateGroup} size='condensed'>
            Criar Grupos
          </Button>
        )}
      </div>
    </div>
  );

  const swapSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setSortOrderLabel(sortOrder === 'asc' ? 'Z-A' : 'A-Z');
  };

  const sortByName = (a: VehicleGroup, b: VehicleGroup) => {
    if (sortOrder === 'asc') return a.name.toLowerCase().localeCompare(b.name.toLowerCase());

    return b.name.toLowerCase().localeCompare(a.name.toLowerCase());
  };

  const sortBySelected = (a: VehicleGroup, b: VehicleGroup) => {
    if (selectedGroupsDraft.find((g) => g.id === a.id) && !selectedGroupsDraft.find((g) => g.id === b.id)) return -1;
    if (!selectedGroupsDraft.find((g) => g.id === a.id) && selectedGroupsDraft.find((g) => g.id === b.id)) return 1;
    return 0;
  };

  const filterOptionsBySearchBoxInput = () => {
    const normalizedSearchBoxInput = searchBoxInput.trim().toLowerCase();
    if (normalizedSearchBoxInput.length > 0) {
      return vehicleGroups.filter((group) => group.name.toLocaleLowerCase().match(normalizedSearchBoxInput));
    }
    return [...vehicleGroups];
  };

  const filteredOptions = filterOptionsBySearchBoxInput();

  const groupsFiltered: VehicleGroup[] = filteredOptions.sort(sortByName).sort(sortBySelected);

  const validateGroupHasNoVehicle = (group: VehicleGroup): boolean => (disableGroupsWithoutVehicle && group.vehicleIds && group.vehicleIds.length === 0) ?? false;

  const disabled = (group: VehicleGroup) =>
    validateGroupHasNoVehicle(group) || (selectedGroups.find((g) => g.id == group.id) === undefined && limit != null && selectedGroups.length >= limit);

  return (
    <Dropdown.Overlay className='w-fit'>
      <Dropdown.Header className='px-2'>
        <div className='flex w-full flex-row justify-between'>
          <Button data-testid='vehicle-group-filter-clean' variant='text' size='condensed' disabled={selectedGroups.length === 0} onClick={onClearFilter}>
            Limpar
          </Button>
          <Button data-testid='vehicle-group-filter-sort' startIcon={<Icon name='cb-sort-menu-asc' />} variant='text' size='condensed' onClick={swapSortOrder}>
            A-Z
          </Button>
        </div>
      </Dropdown.Header>
      <Dropdown.Search className='px-2'>
        <InputSearch data-testid='vehicle-group-filter-search-input' autoFocus placeholder='Procurar grupos' className='w-full' onChange={setSearchBoxInput} />
      </Dropdown.Search>
      <Dropdown.Body className='px-2'>
        <Dropdown.List
          heightList={300}
          heightItem={28}
          data={groupsFiltered}
          isLoading={isLoading}
          placeholderComponent={renderNoGroupsPlaceholder()}
          loadingComponent={<Dropdown.Loading />}
          itemComponent={(group: VehicleGroup) => {
            const checkbox = (
              <div className='flex items-center gap-2'>
                <Checkbox
                  id={group.name}
                  data-testid={`vehicle-group-filter-${group.id}`}
                  data-cy={`vehicle-group-filter-${group.id}`}
                  disabled={disabled(group)}
                  checked={selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)}
                  onCheckedChange={() => onChange(group)}
                  value={group.name}
                  title={group.name}
                />
                <Label htmlFor={group.name} className='line-clamp-1 text-start' title={group.name}>
                  {group.name}
                </Label>
              </div>
            );

            return (
              <div key={group.name} className='line-clamp-1 w-full pr-4'>
                <Tooltip enable={validateGroupHasNoVehicle(group)} content='Grupo sem veículos'>
                  {checkbox}
                </Tooltip>
              </div>
            );
          }}
        />
      </Dropdown.Body>
      <Dropdown.Footer>
        <Dropdown.Actions className='flex flex-row justify-between px-2'>
          <div className='flex flex-row'>
            <Typography className='font-bold'>{`${selectedGroups.length}${limit ? '/' + limit : ''}`}</Typography>
            &nbsp;
            <Typography>selecionados</Typography>
          </div>
          <div className='ml-4 flex flex-row'>
            <Button data-testid='vehicle-group-filter-cancel' variant='text' size='condensed' onClick={onCancel} className='px-4'>
              Cancelar
            </Button>
            <Button
              data-testid='vehicle-group-filter-apply'
              size='condensed'
              onClick={onApplyFilter}
              disabled={vehicleGroups.length === 0 || !selectionChanged}
              className='ml-2 px-7'
            >
              Aplicar
            </Button>
          </div>
        </Dropdown.Actions>
      </Dropdown.Footer>
    </Dropdown.Overlay>
  );
};

export default MultiSelectVehicleGroupsSidebarOverlay;
